import { useRef } from "react";
import {
    useMutation,
    type DefaultError,
    type UseMutationOptions,
    type UseMutationResult,
} from "@tanstack/react-query";

/**
 * A mutation that runs once per press, however fast the presses come.
 *
 * A button is disabled once its mutation is pending, but React Query
 * tells the page that a tick later, and presses can land in between: a
 * script, a double tap, a held Enter. Five presses there are five Stripe
 * sessions, five "sent" records. Here a press is dropped, at the press,
 * while one with the same key is still running; the key is the
 * variables by default, so two different postings can be sent at once
 * and the same one cannot be sent twice. The key is let go when the
 * mutation's work ends, whatever its outcome.
 *
 * `mutateAsync` is guarded the same way: a dropped press resolves to
 * undefined and runs nothing.
 */
export function useSingleFlightMutation<
    TData = unknown,
    TError = DefaultError,
    TVariables = void,
    TContext = unknown,
>(
    options: UseMutationOptions<TData, TError, TVariables, TContext>,
    keyOf: (variables: TVariables) => string = (variables) => JSON.stringify(variables ?? null),
): UseMutationResult<TData, TError, TVariables, TContext> {
    const inFlight = useRef(new Set<string>());
    const run = options.mutationFn;
    const mutation = useMutation<TData, TError, TVariables, TContext>({
        ...options,
        mutationFn: run
            ? async (variables, context) => {
                  try {
                      return await run(variables, context);
                  } finally {
                      inFlight.current.delete(keyOf(variables));
                  }
              }
            : undefined,
    });

    const claim = (variables: TVariables): boolean => {
        const key = keyOf(variables);
        if (inFlight.current.has(key)) return false;
        inFlight.current.add(key);
        return true;
    };

    return {
        ...mutation,
        mutate: ((...args: Parameters<typeof mutation.mutate>) => {
            if (claim(args[0] as TVariables)) mutation.mutate(...args);
        }) as typeof mutation.mutate,
        mutateAsync: ((...args: Parameters<typeof mutation.mutateAsync>) =>
            claim(args[0] as TVariables)
                ? mutation.mutateAsync(...args)
                : Promise.resolve(undefined as TData)) as typeof mutation.mutateAsync,
    } as UseMutationResult<TData, TError, TVariables, TContext>;
}

/** The key for a mutation that may run once at a time, whatever it is given. */
export const ONE_AT_A_TIME = () => "one";
